import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { ANI_ALI } from '@rsos/components/capstone/Location/constants/dataSources';
import {
  getDataSource,
  isSourceAniAli,
} from '@rsos/components/capstone/Location/helpers';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import ICON_MAPPING from '@rsos/components/capstone/common/Map/assets/calls/sprites-output/sprites-info.json';
import callsSprites from '@rsos/components/capstone/common/Map/assets/calls/sprites-output/sprites_2x.png';
import {
  showIncidentPopover,
  hideIncidentPopover,
} from '@rsos/components/capstone/common/Map/useDeckGL/MapPopover/mapPopoverSlice';
import CallsLayer, {
  CALL_ICONS_LAYER,
  SECONDARY_LOCATION_ICON_LAYER,
} from '@rsos/components/capstone/common/Map/useDeckGL/RSOSLayers/CallsLayer';
import storeLayers from '@rsos/components/capstone/common/Map/useDeckGL/storeLayers';
import { normalizeLocation } from '@rsos/components/capstone/common/Map/useDeckGL/utils';
import {
  BE_CALL_ALERTING,
  BE_CALL_ANSWERED,
  BE_HOLD_CALL,
  BE_END_CALL,
  BE_RETRIEVE_CALL,
} from '@rsos/constants/callStatuses';
import { SOURCE_SMS } from '@rsos/constants/locationSources';
import { LANDLINE, VOIP, OTHER, PHASE } from '@rsos/constants/locationTypes';
import useIsGoogleMapsFeatureFlagEnhanced from '@rsos/hooks/useIsGoogleMapsFeatureFlagEnhanced';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { ESRI, GOOGLE } from '@rsos/maps/constants/mapProviders';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setHoveredCallerID } from '@rsos/reduxDataStore/src/features/incidentInteractions/hoveredIncidentSlice';
import {
  useCallQueueFilters,
  callQueueSelector,
} from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { workstationPositionSelector } from '@rsos/reduxDataStore/src/features/selectors/workstationSelectors';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import getMostRecentCallStatus from '@rsos/utils/getMostRecentCallStatus';
import {
  getMostRecentLoc,
  getMostRecentLocations,
  getMostRecentLocationWithLocation,
} from '@rsos/utils/getMostRecentLoc';
import { isCallerIDFromOrg } from '@rsos/utils/isCallerIDFromOrg';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import { captureException } from '@rsos/utils/sentry';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePortalType from '@rsos/utils/usePortalType';
import { isIgnoreType } from '../../../JurisdictionView/Map/markers/calls/ignoreUtils';
import { isExtraInfoOrMeta } from '../../../JurisdictionView/helpers';
import {
  trackHoverOnCallWithTime,
  trackHoverOnPin,
  trackHoverOnCall,
  trackClickOnCallFromMap,
  trackCallMarkerClicks,
} from '../../../JurisdictionView/jurisdictionViewTrackings';
import { getMarkerColor, shouldMarkerDim } from '../../utils/callFiltering';

// TODO v2: there are many imports to emergency_data. consider moving them out or adding new

const getPrimaryCallIconName = ({
  location,
  isAllView,
  isHistoricLocation,
  hasCallStatusAccess,
  callQueue,
  hasAniAliAccess,
}) => {
  const callStatus = getMostRecentCallStatus({
    callerID: location?.caller_id,
    callQueue,
    hasAniAliAccess,
    hasCallStatusAccess,
  });

  const isCallRinging = callStatus === BE_CALL_ALERTING;
  const isCallAnswered =
    callStatus === BE_CALL_ANSWERED || callStatus === BE_RETRIEVE_CALL;
  const isCallOnHold = callStatus === BE_HOLD_CALL;
  const isCallReleased = callStatus === BE_END_CALL;
  if (isAllView) {
    if (location?.source === SOURCE_SMS || location?.isTT911) {
      return 'allcall_textto911';
    } else if (hasCallStatusAccess && callStatus) {
      if (isCallRinging) {
        return 'call_status_ringing';
      } else if (isCallAnswered) {
        return 'call_status_answered';
      } else if (isCallOnHold) {
        return 'call_status_on_hold';
      } else if (isCallReleased) {
        return 'call_status_released';
      } else {
        return 'allcall_marker';
      }
    } else {
      return 'allcall_marker';
    }
  } else {
    if (isHistoricLocation) {
      return 'historic_call_marker';
    } else if (location?.source === SOURCE_SMS || location?.isTT911) {
      return 'sms_selected';
    } else {
      return 'call_selected';
    }
  }
};

const getSecondaryCallIconName = ({ isAllView, location }) => {
  const isLandlineOrOthers =
    (location && location?.class_of_service_category === LANDLINE) ||
    (location && location?.class_of_service_category === OTHER);

  const isVOIP = location?.class_of_service_category === VOIP;
  // TODO: add these icons to the sprite

  if (isAllView) {
    return 'allcall_marker';
  } else {
    if (isLandlineOrOthers) {
      return 'call_landline';
    } else if (isVOIP) {
      return 'call_voip';
    } else {
      return 'call_ali';
    }
  }
};

const MARKER_SIZES = {
  sizeScale: 60, // this is the factor to scale the icon.
  sizeMinPixels: 38, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 40, // when zoomed in, the max height of the icon.
};

// Constant for Google Map default zoom state.
const GOOGLE_MAP_SELECTED_ZOOM_STATE = {
  hasChanged: false, // Tracks if the default zoom level has changed.
  level: DEFAULT_ZOOM_LEVEL, // Default zoom level when a call is selected.
};

const CallsGL = ({ map, overlay, activeQueueTab, isMapIconDisabled }) => {
  const [hoveredCallData, setHoveredCallData] = useState(null);
  const [normalizedData, setNormalizedData] = useState({
    primaryLocations: [],
    secondaryLocations: [],
  });
  const [timer, setTimer] = useState(0);
  const hoveredTimer = useRef(null);

  // Call taker mode filtering
  const { callFilters, hasActiveCallFilters } = useCallQueueFilters();
  const workStation = useSelector(workstationPositionSelector);

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const callQueue = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  // Helper function to check if call should be dimmed for call taker mode
  const checkShouldDim = useCallback(
    callerID =>
      shouldMarkerDim(callerID, {
        activeQueueTab,
        hasActiveCallFilters,
        callQueue,
        callFilters,
        workStation,
      }),
    [activeQueueTab, hasActiveCallFilters, callFilters, workStation, callQueue],
  );

  // State hook to track Google map zoom listener.
  const [googleMapZoom, setGoogleMapZoom] = useState(
    GOOGLE_MAP_SELECTED_ZOOM_STATE,
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );
  const what3wordsCoordinates = useSelector(
    state => state.what3words?.searched?.coordinates,
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const { userPannedAway } = useZoomAndLocationContext();

  const { portalType } = usePortalType();

  const hasCallStatusAccess =
    useSelector(state =>
      checkCallStatusAccessSelector({
        psaps: state.psaps,
      }),
    ) && hasAniAliAccess;

  const selectedCall = useMemo(
    () => callQueue[selectedCallerID],
    [callQueue, selectedCallerID],
  );

  const mostRecentLoc = getMostRecentLoc(selectedCall);
  const isValidCoords = isValidCoordinates(mostRecentLoc);

  const isAllView = useMemo(
    () =>
      (!selectedAlertID && !selectedCallerID) ||
      isIgnoreType(selectedCall) ||
      !isValidCoords,
    [selectedAlertID, selectedCallerID, selectedCall, isValidCoords],
  );

  const { irpVersion } = useIsIRP();
  const dispatch = useDispatch();

  const rspDeckGL = useIsGoogleMapsFeatureFlagEnhanced();

  const handleSecondaryLocationTracking = useCallback(
    location => {
      const { payloadID, requestID } = isExtraInfoOrMeta(location);
      const isConnectedCar = isCallerIDFromOrg(
        'sxm',
        callQueue,
        location?.caller_id,
      );

      const callStatus = getMostRecentCallStatus({
        callerID: location?.caller_id,
        callQueue,
        hasAniAliAccess,
        hasCallStatusAccess,
      });
      trackHoverOnCall(
        isConnectedCar,
        irpVersion,
        payloadID,
        requestID,
        portalType,
        ANI_ALI,
        location.class_of_service,
        callStatus,
      );
    },
    [irpVersion, portalType, callQueue, hasAniAliAccess, hasCallStatusAccess],
  );

  // handles hovering over Secondary Location
  const handleHoverSecondaryLocation = useCallback(
    ({ location }) => {
      if (map) {
        const isSecondaryLocation =
          location?.class_of_service_category?.includes(PHASE);
        if (location && isSecondaryLocation) {
          // SECONDARY LOCATION
          setHoveredCallData(location);
          dispatch(showIncidentPopover(location));
          handleSecondaryLocationTracking(location);
          overlay.setCursorPointer();
        } else {
          setHoveredCallData(null);
          dispatch(hideIncidentPopover());
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, handleSecondaryLocationTracking, map, overlay],
  );

  /**
   * Tracking function for primary locations
   * @param {Object} location - Primary location
   * @param {Boolean} timerBased - Flag to determine which tracking functions
   * to invoke
   * @param {Number} time - Number of seconds a hover occurred
   */
  const handlePrimaryLocationTracking = useCallback(
    (location, timerBased = false, time = 0) => {
      const isAniAli = isSourceAniAli(location);
      const serviceType = isAniAli ? ANI_ALI : getDataSource(location);
      const classOfServices = isAniAli
        ? location?.class_of_service || null
        : null;

      const callStatus = getMostRecentCallStatus({
        callerID: location?.caller_id,
        callQueue,
        hasAniAliAccess,
        hasCallStatusAccess,
      });

      const isConnectedCar = isCallerIDFromOrg(
        'sxm',
        callQueue,
        location?.caller_id,
      );

      const { payloadID, requestID } = isExtraInfoOrMeta(location);
      if (!timerBased) {
        trackHoverOnPin(irpVersion, serviceType, classOfServices, callStatus);
      } else {
        if (time >= 1 && time < 2) {
          trackHoverOnCallWithTime(
            1,
            'map',
            isConnectedCar,
            irpVersion,
            payloadID,
            requestID,
            portalType,
            serviceType,
            classOfServices,
            callStatus,
          );
        }
        if (time >= 2) {
          trackHoverOnCallWithTime(
            2,
            'map',
            isConnectedCar,
            irpVersion,
            payloadID,
            requestID,
            portalType,
            serviceType,
            classOfServices,
            callStatus,
          );
        }
      }
    },
    [irpVersion, portalType, callQueue, hasAniAliAccess, hasCallStatusAccess],
  );

  // handles hovering over Primary Location
  const handleHoverPrimaryLocation = useCallback(
    ({ location }) => {
      if (map) {
        if (location) {
          setHoveredCallData(location);
          dispatch(showIncidentPopover(location));
          handlePrimaryLocationTracking(location, false, 0);
        } else {
          setHoveredCallData(null);
          dispatch(hideIncidentPopover());
          // Reset timer for timer based tracking
          setTimer(0);
          overlay.setCursorDefault();
        }
      }
    },
    [dispatch, handlePrimaryLocationTracking, map, overlay],
  );

  // Timer based tracking
  useEffect(() => {
    if (hoveredCallData) {
      if (timer < 2) {
        hoveredTimer.current = setTimeout(
          () => setTimer(timer => timer + 1),
          1000,
        );
      }

      if (timer >= 1 && timer < 2) {
        handlePrimaryLocationTracking(hoveredCallData, true, timer);
      }
      if (timer >= 2) {
        handlePrimaryLocationTracking(hoveredCallData, true, 2);
      }
    } else {
      setTimer(0);
      clearTimeout(hoveredTimer.current);
    }
    return () => {
      clearTimeout(hoveredTimer.current);
    };
  }, [handlePrimaryLocationTracking, hoveredCallData, timer]);

  // Normalize the data
  useEffect(() => {
    if (isAllView) {
      if (Object.keys(callQueue).length) {
        const locations = Object.values(callQueue)
          .filter(call => !isIgnoreType(call))
          .map(call => {
            const { mostRecentLocation, mostRecentSecondaryLocation } =
              getMostRecentLocations(call);

            const recentLocation = normalizeLocation(
              mostRecentLocation || mostRecentSecondaryLocation,
            );

            // All view prioritizes DBH locations
            if (rspDeckGL) {
              return {
                ...recentLocation,
                isHistoric: false,
                callStatuses: call?.callStatuses,
              };
            } else {
              // ONLY POPULATE PRIMARY LOCATION for mostRecentSecondaryLocation in ALL VIEW
              const location = !mostRecentLocation
                ? mostRecentSecondaryLocation
                : undefined;

              const recentLocation = normalizeLocation(location);

              if (recentLocation) {
                return {
                  ...recentLocation,
                  isHistoric: false,
                  callStatuses: call?.callStatuses,
                };
              }
            }
          })
          .filter(loc => loc);
        const data = {
          primaryLocations: locations,
          secondaryLocations: [],
        };
        setNormalizedData(data);
      } else {
        // All calls have been removed
        setNormalizedData({
          primaryLocations: [],
          secondaryLocations: [],
        });
      }
    } else if (selectedCallerID) {
      if (
        callQueue[selectedCallerID]?.core?.locations?.length ||
        callQueue[selectedCallerID]?.core?.secondaryLocations?.length
      ) {
        const call = callQueue[selectedCallerID];
        const locations = call?.core?.locations?.map((location, idx) => {
          const recentLocation = normalizeLocation(location);
          return {
            ...recentLocation,
            isHistoric: idx < call?.core?.locations?.length - 1,
            callStatuses: call?.callStatuses,
          };
        });
        const secondaryLocation =
          call?.core?.secondaryLocations[
            call?.core?.secondaryLocations?.length - 1
          ];

        // Primary Location is populated, but only visible on All View
        // It is being handled in Markers.js for event view
        const data = {
          primaryLocations: rspDeckGL && locations ? locations : [],
          secondaryLocations: secondaryLocation ? [secondaryLocation] : [], // Only one is drawn at any time
        };

        setNormalizedData(data);
      }
    }
  }, [
    callQueue,
    isAllView,
    selectedCallerID,
    dispatch,
    map,
    numberWithNewLocation,
    rspDeckGL,
  ]);

  // Draw the markers for all view or detailed view
  useEffect(() => {
    if (normalizedData) {
      // Creating call icon layers with call taker mode filtering
      const primaryCallsIconLayerProps = {
        data: normalizedData.primaryLocations,
        iconAtlas: callsSprites,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'meters',
        visible: !selectedAlertID,
        pickable: !isMapIconDisabled,
        highlightColor: [255, 255, 255, 60],

        getIcon: d => {
          return getPrimaryCallIconName({
            location: d,
            isAllView,
            isHistoricLocation: d?.isHistoric,
            hasCallStatusAccess,
            callQueue,
            hasAniAliAccess,
          });
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        getColor: d => {
          return getMarkerColor(d?.caller_id, activeQueueTab, checkShouldDim);
        },
        ...MARKER_SIZES,
      };

      const mostRecentLocation =
        normalizedData?.primaryLocations[
          normalizedData?.primaryLocations?.length - 1
        ] || {}; // Most recent location is last

      const commonCircleLayerProps = {
        extruded: true,
        filled: true,
        getLineWidth: 2,
        lineWidthMaxPixels: 2,
        lineWidthMinPixels: 1,
        pickable: false, // If true, the layers respond to pointer picking events
        pointType: 'circle',
        stroked: true,
        radiusMinPixels: 1,

        getFillColor: () => {
          return [6, 192, 139, 51];
        },
        getLineColor: () => {
          return [6, 192, 139, 255];
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        getRadius: d => {
          return d?.uncertainty_radius;
        },
      };
      const mostRecentCircleLayerProps = {
        data: [mostRecentLocation],
        visible: !!selectedCallerID,
        ...commonCircleLayerProps,
      };

      const historicCircleLayerProps = {
        data:
          hoveredCallData !== null &&
          !isEqual(hoveredCallData, mostRecentLocation)
            ? [hoveredCallData]
            : [],
        visible: true, // This will only render when there is data. Safe to set as true. Doesn't conflict with the most recent circle
        ...commonCircleLayerProps,
      };
      const secondaryCallsIconLayerProps = {
        iconAtlas: callsSprites,
        iconMapping: ICON_MAPPING,
        sizeUnits: 'meters',
        visible: !!selectedCallerID,
        pickable: !isMapIconDisabled,
        highlightColor: [255, 255, 255, 60],

        getIcon: d => {
          return getSecondaryCallIconName({ isAllView, location: d });
        },
        getPosition: d => {
          return [d?.longitude, d?.latitude];
        },
        getColor: d => {
          return getMarkerColor(d?.caller_id, activeQueueTab, checkShouldDim);
        },
        ...MARKER_SIZES,
      };

      // Won't get drawn when only 1 set of coordinates exist
      // Get primary location from Redux directly when rspDeckGL is false
      const primaryLocationForPath = rspDeckGL
        ? mostRecentLocation
        : callQueue[selectedCallerID]?.core?.locations?.[
            callQueue[selectedCallerID]?.core?.locations?.length - 1
          ];

      const pathData = [
        [
          [primaryLocationForPath?.longitude, primaryLocationForPath?.latitude],
          ...(normalizedData.secondaryLocations[0]
            ? [
                [
                  normalizedData.secondaryLocations?.[0]?.longitude,
                  normalizedData.secondaryLocations?.[0]?.latitude,
                ],
              ]
            : []),
        ],
      ];
      const secondaryCallPathLayerProps = {
        data: pathData,
        pickable: false,
        visible: !!selectedCallerID,
      };

      const callsLayer = new CallsLayer({
        data: normalizedData,
        primaryCallsIconLayerProps,
        mostRecentCircleLayerProps,
        historicCircleLayerProps,
        secondaryCallsIconLayerProps,
        secondaryCallPathLayerProps,
        autoHighlight: true,

        // Pointer picking events functions must be passed in this way instead of through the layerProps
        onClick: d => {
          if (d?.sourceLayer?.id.includes(CALL_ICONS_LAYER)) {
            const id = d.object?.caller_id;
            setHoveredCallData(null);
            dispatch(setSelectedCallerID(id));
            dispatch(setHoveredCallerID(null));
            dispatch(hideIncidentPopover());
            trackCallMarkerClicks(activeQueueTab, irpVersion);
            trackClickOnCallFromMap(irpVersion);
          }
        },

        onHover: d => {
          /**
           * @constant isSameLocObj - prevent re-computation when cursor moves inside the marker
           */

          const isSameLocObj =
            hoveredCallData &&
            d?.object &&
            hoveredCallData.caller_id === d?.object?.caller_id &&
            hoveredCallData.call_start_time === d?.object?.call_start_time;

          if (!isSameLocObj) {
            const layerID = d?.sourceLayer?.id;
            const isSecondaryLocation =
              layerID.includes(SECONDARY_LOCATION_ICON_LAYER) && d?.object;

            if (isSecondaryLocation) {
              handleHoverSecondaryLocation({ location: d.object });
              dispatch(setHoveredCallerID(d.object.caller_id));
            } else if (d?.object) {
              handleHoverPrimaryLocation({ location: d.object });
            } else {
              dispatch(setHoveredCallerID(null));
              setHoveredCallData(null);
              dispatch(hideIncidentPopover());
              overlay.setCursorDefault();
            }
          }
        },
      });
      if (overlay) {
        storeLayers.save(callsLayer);

        // Currently the "callsLayer" is only used for Google map. If the map provider is switched
        // to Esri map, delete the "callsLayer" from the layers collection.
        if (mapProvider === ESRI && storeLayers.getAll().includes(callsLayer)) {
          storeLayers.deleteLayer('CallsLayer');
        }

        overlay.setLayers(storeLayers.getAll());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeQueueTab,
    dispatch,
    handleHoverSecondaryLocation,
    hasCallStatusAccess,
    hoveredCallData,
    isAllView,
    normalizedData,
    overlay,
    selectedAlertID,
    selectedCallerID,
    callQueue,
    map,
    isMapIconDisabled,
  ]);

  /**
   * Reset zoom when changing selected call and we are zoomed
   * out past the default
   */
  useEffect(() => {
    if (googleMapZoom.level < GOOGLE_MAP_SELECTED_ZOOM_STATE.level) {
      setGoogleMapZoom({
        hasChanged: true,
        level: GOOGLE_MAP_SELECTED_ZOOM_STATE.level,
      });
    }
    // We only want to reset the zoom level when the selectedCallerID changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCallerID]);

  useEffect(() => {
    if (selectedCall && mapProvider === GOOGLE && map) {
      const { mostRecentLocation, mostRecentSecondaryLocation } =
        getMostRecentLocations(selectedCall);

      const mostRecentLoc = getMostRecentLocationWithLocation(selectedCall);

      // Get the Google map zoom level when a call is selected.
      // If the zoom level and the theme or mapId changes, retain the zoom level.
      const getZoomLevel = () => {
        // Only change the zoom level if the user is zoomed out
        // passed the default.
        return map.mapInstance.getZoom() < DEFAULT_ZOOM_LEVEL
          ? DEFAULT_ZOOM_LEVEL
          : null;
      };

      // Handling the panning of the map when selecting a call and when there are location updates
      if (rspDeckGL) {
        /**
         * @constant {shouldPan} - condition logic for map panning with deck.gl.
         * When a call is selected, handles panning the map when either the
         * most recent primary location or most recent secondary location is
         * updated.
         */
        const shouldZoom =
          !isIgnoreType(mostRecentLoc) &&
          !!(
            (mostRecentLocation && selectedCall && !what3wordsCoordinates) ||
            (!mostRecentLocation &&
              mostRecentSecondaryLocation &&
              !what3wordsCoordinates)
          );

        const zoom = getZoomLevel();

        const shouldPan =
          !isAllView &&
          !isIgnoreType(mostRecentLoc) &&
          !userPannedAway &&
          mostRecentLoc;

        if (shouldPan) {
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map.mapInstance,
            latitude: mostRecentLoc.latitude,
            longitude: mostRecentLoc.longitude,
            zoom: shouldZoom ? zoom : null,
          });
        }
      } else {
        /**
         * @constant {shouldPan} - condition logic for map panning
         * when selecting a call with both DBH and SecondaryLocation:
         * The map will shifts between different locations.
         * This occurs because of a conflict of interest between the two layers (Google & deckGL).
         * Google pulls the selected caller ID into one location initially.
         * Then, deckGL promptly moves it to another location.
         */
        const zoom = getZoomLevel();

        const shouldPan =
          !userPannedAway && !isIgnoreType(mostRecentLoc) && mostRecentLoc;

        if (shouldPan) {
          panMapTo({
            mapProvider: GOOGLE,
            mapInstance: map.mapInstance,
            latitude: mostRecentLoc.latitude,
            longitude: mostRecentLoc.longitude,
            zoom,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map.mapInstance,
    mapProvider,
    selectedCall,
    selectedCallerID,
    what3wordsCoordinates,
    userPannedAway,
  ]);

  // Google maps zoom change listener to track the map zoom level.
  // This useEffect is triggered when a 911 event or alert is selected and the theme/mapId changes,
  // retain the zoom level if it is not set to the default zoom level of 16.
  useEffect(() => {
    let zoomChangeListener;

    const handleZoomChange = () => {
      setGoogleMapZoom({
        hasChanged: true,
        level: map.mapInstance.getZoom(),
      });
    };

    if (map.mapInstance && mapProvider === GOOGLE && selectedCallerID) {
      try {
        zoomChangeListener = window.google.maps.event.addListener(
          map.mapInstance,
          'zoom_changed', // https://developers.google.com/maps/documentation/javascript/events
          handleZoomChange,
        );
      } catch (error) {
        captureException(error);
      }

      return () => {
        if (zoomChangeListener) {
          try {
            window.google.maps.event.removeListener(zoomChangeListener);
          } catch (error) {
            captureException(error);
          }
        }
      };
    }
  }, [map.mapInstance, mapProvider, selectedCallerID]);

  return <></>;
};

export default CallsGL;
